import React, { useEffect, useState } from "react"
import { Spin, Alert } from "antd"
import {
  fetchSelectedProductV2,
  purposeLabel,
  loanTypeLabel,
  refinanceProgramLabel,
  subjectPropertyLabel,
  maxLoanSizeLabel,
  payoutDisplay,
  payoutLabel,
  isRefinancePurpose,
  formatMoney,
  interestPaid,
  estimatedMarketValue,
  remainingLenderCredit,
} from "./api"
import "./LoanV2.css"

const YEARS_TO_HOLD = 7

function Field({ label, children }) {
  return (
    <div className="loan-v2-field">
      <div className="loan-v2-label">{label}</div>
      <div className="loan-v2-value">{children}</div>
    </div>
  )
}

function FieldGrid({ children }) {
  return <div className="loan-v2-grid">{children}</div>
}

function paydownLabel(purpose) {
  switch (purpose) {
    case "purchase":
      return "Downpayment"
    case "refinance":
    case "ratetermrefinance":
      // Match Fee Sheet Overview — borrower cash to reduce principal.
      return "Paydown principal"
    case "cashoutrefinance":
      return "Cash out"
    default:
      return "Cash"
  }
}

function paydownValue(purpose, product) {
  if (!product) return "-"
  if (purpose === "purchase") return formatMoney(product.downPayment)
  if (purpose === "refinance" || purpose === "ratetermrefinance") {
    return formatMoney(product.paydown)
  }
  if (purpose === "cashoutrefinance") return formatMoney(product.cashOut)
  return "-"
}

/**
 * Read-only selected product card fed only by /los/v2/.../selected-product.
 * Layout: 5 columns per row, left-aligned.
 */
export default function LoanV2({ loanId }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancelled = false
    if (!loanId) {
      setLoading(false)
      setError("missing loan id")
      return
    }
    setLoading(true)
    setError("")
    const token = sessionStorage.getItem("ZeitroA")
    fetchSelectedProductV2(loanId, token)
      .then((js) => {
        if (!cancelled) {
          setData(js)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || "Failed to load selected product")
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [loanId])

  if (loading) {
    return (
      <div className="loan-v2-loading">
        <Spin /> Loading selected product…
      </div>
    )
  }

  if (error) {
    return (
      <div className="mb-4 mt-2">
        <Alert type="warning" message={`Loan V2: ${error}`} showIcon />
      </div>
    )
  }

  if (!data?.hasSelection || !data.product) {
    return (
      <div className="mb-4 mt-2">
        Loan product has not been selected yet.
      </div>
    )
  }

  const product = data.product
  const property = data.property || {}
  const purpose = property.purpose
  const prepaidDays = product.prepaidDays != null ? product.prepaidDays : "-"
  const interest7 = interestPaid(product, YEARS_TO_HOLD)
  const interestLife = interestPaid(product, product.term)

  return (
    <div className="mb-4 mt-2 loan-v2-readonly">
      <div className="loan-v2-panel">
        <FieldGrid>
          <Field label="Purpose of Loan">{purposeLabel(purpose)}</Field>
          <Field label="Loan Amount">{formatMoney(product.loanSize, 0)}</Field>
          <Field label="Estimated Market Value">
            {formatMoney(estimatedMarketValue(property), 0)}
          </Field>
          {purpose === "purchase" && (
            <Field label="Purchase Price">
              {formatMoney(property.salesPrice, 0)}
            </Field>
          )}
          <Field label={paydownLabel(purpose)}>
            {paydownValue(purpose, product)}
          </Field>
          <Field label={payoutLabel(purpose)}>
            {formatMoney(payoutDisplay(product, purpose), purpose === "refinance" || purpose === "ratetermrefinance" ? 2 : 0)}
          </Field>

          <Field label="Loan Type">{loanTypeLabel(product)}</Field>
          <Field label="Term">{product.term} years</Field>
          <Field label="Rate">{product.baseRate}%</Field>
          <Field label="APR">{product.apr}%</Field>
          <Field label="Mo. Payment (P&I)">
            {formatMoney(product.monthlyPayment, 0)}
          </Field>

          <Field label={`Interest over ${YEARS_TO_HOLD} years`}>
            {formatMoney(interest7)}
          </Field>
          <Field label="Interest over loan life">
            {formatMoney(interestLife)}
          </Field>
          <Field label={`Prepaid interest (${prepaidDays} days)`}>
            {formatMoney(product.prepaidInterest)}
          </Field>
          <Field label="Points">{product.points}</Field>

          <Field label="Remaining lender credit">
            {formatMoney(remainingLenderCredit(product))}
          </Field>
          {!!product.lenderFee && (
            <Field label="Lender fee">{formatMoney(product.lenderFee)}</Field>
          )}
          <Field label="Minimal reserves">
            {product.minMonthsReserves} months
          </Field>

          {isRefinancePurpose(purpose) && (
            <>
              <Field label="Current Program">
                {refinanceProgramLabel(property.refinanceProgram)}
              </Field>
              {String(data.currentRate ?? "").trim() !== "" && (
                <Field label="Current Rate">{String(data.currentRate).trim()}%</Field>
              )}
            </>
          )}

          <Field label="Subject property">
            {subjectPropertyLabel(property)}
          </Field>
          <Field label="County">{property.county || "-"}</Field>
          {/* V2: hide Maximum loan size for now
          <Field label="Maximum loan size">{maxLoanSizeLabel(property)}</Field>
          */}
          {product.maxCashOut != null && (
            <Field label="Maximum cash out">
              {formatMoney(product.maxCashOut)}
            </Field>
          )}
        </FieldGrid>

        {(product.highCostMortgage ||
          product.features?.hasPrepaymentPenalty) && (
          <div className="loan-v2-flags">
            {product.highCostMortgage ? (
              <div>This is a high cost loan</div>
            ) : null}
            {product.features?.hasPrepaymentPenalty ? (
              <div>
                Has prepayment penalty:{" "}
                {product.features.prepaymentPenaltyAmount}, term:{" "}
                {product.features.prepaymentPenaltyAmount} over{" "}
                {product.features.prepaymentPenaltyTerm} months.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
